// src/user/ReferralTreeLazy.js
// Community Network: lazy-loaded referral hierarchy (lightweight org-style layout; no MUI TreeView).
import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalculateIcon from "@mui/icons-material/Calculate";
import { Link as RouterLink } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API, API_BASE_URL } from "../config/apiConfig";
import DashboardPageHeader from "../components/DashboardPageHeader";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import {
  readUserSessionProfileCache,
  writeUserSessionProfileCache,
} from "../utils/userDashboardHomeCache";

/** Loaded tree rows from process.mlm_GetDirectReferralsWithDescendants (UI cap). */
const COMMUNITY_NETWORK_MAX_DEPTH = 10;
/** Must match @CcDownlineMaxDepth in mlm_GetEarningsSummary (15 = same cap as rank placement). */
const COMMUNITY_TOTAL_DEPTH = 15;

function nodeMatchesSearch(node, term) {
  const q = String(term || "").trim().toLowerCase();
  if (!q || !node) return false;
  return [node.name, node.userName, node.firstName, node.lastName, node.email, node.referralCode]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(q));
}

/** Prefer a downline match; fall back to the root (you) if nothing else matches. */
function findPathToMatch(node, term, path = []) {
  if (!node) return null;
  const nextPath = [...path, node.id];
  const isRoot = path.length === 0;
  if (!isRoot && nodeMatchesSearch(node, term)) return nextPath;
  for (const child of node.children || []) {
    const found = findPathToMatch(child, term, nextPath);
    if (found) return found;
  }
  if (isRoot && nodeMatchesSearch(node, term)) return nextPath;
  return null;
}

const ReferralTreeLazy = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserCode, setCurrentUserCode] = useState(null);
  const [rootNode, setRootNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  /** Reset when re-initializing so the root auto-expands again after Refresh. */
  const lastExpandedTreeRootId = useRef(null);
  const orgChartScrollRef = useRef(null);
  const searchExpandTimerRef = useRef(null);
  const [loadedChildren, setLoadedChildren] = useState(new Map()); // Map<referralCode, children[]>
  const [loadingChildren, setLoadingChildren] = useState(new Set()); // Set<referralCode>
  const [searchTerm, setSearchTerm] = useState("");
  const [searchMessage, setSearchMessage] = useState("");
  const [highlightedId, setHighlightedId] = useState(null);
  const [totalCommunityConnections, setTotalCommunityConnections] = useState(0); // Total connections from user profile
  const [communityLevelCounts, setCommunityLevelCounts] = useState([]);
  const [communityMaxDepth, setCommunityMaxDepth] = useState(0);

  // Fetch current user info and initialize root node
  const initializeRoot = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setHighlightedId(null);
      setSearchMessage("");
      lastExpandedTreeRootId.current = null;

      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found. Please log in again.");
        setLoading(false);
        return;
      }

      const decoded = jwtDecode(token);
      const userId = decoded.UserID || decoded.userId;
      setCurrentUserId(userId);

      // Fetch user profile to get referral code once per login session.
      let profilePayload = readUserSessionProfileCache(userId)?.profile;
      if (!profilePayload) {
        const profileResponse = await axios.get(API.userProfile(userId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        profilePayload = profileResponse.data;
        writeUserSessionProfileCache(userId, profilePayload);
      }

      const user = profilePayload?.user || profilePayload;
      const referralCode = user.ReferralCode;
      setCurrentUserCode(referralCode);
      
      // Fetch cached level counts + earnings summary (fast totals)
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [countsResponse, earningsResponse] = await Promise.all([
          axios.get(
            API.downlineLevelCounts(userId, { treeType: 'COMMUNITY', maxLevel: COMMUNITY_TOTAL_DEPTH }),
            { headers }
          ),
          axios.get(`${API.earningsSummary}/${userId}`, { headers }),
        ]);

        if (countsResponse.data?.success) {
          setCommunityLevelCounts(countsResponse.data.levelCounts || []);
          setCommunityMaxDepth(Number(countsResponse.data.maxDepth) || 0);
          const cachedTotal = Number(countsResponse.data.totalCount) || 0;
          if (cachedTotal > 0) {
            setTotalCommunityConnections(cachedTotal);
          }
        }

        if (earningsResponse.data?.success && earningsResponse.data?.earnings) {
          const earnings = earningsResponse.data.earnings;
          const earningsTotal =
            Number(earnings.ActiveConnections ?? earnings.activeConnections ?? 0) || 0;
          if (earningsTotal > 0) {
            setTotalCommunityConnections((prev) => Math.max(prev, earningsTotal));
          }
        }
      } catch (statsErr) {
        console.error('❌ Error fetching downline level counts / earnings:', statsErr);
      }

      // Create root node
      const root = {
        id: `user-${userId}`,
        userId: userId,
        referralCode: referralCode,
        name: `${user.FirstName || ""} ${user.LastName || ""}`.trim() || user.UserName || "You",
        userName: user.UserName || "",
        firstName: user.FirstName || "",
        lastName: user.LastName || "",
        email: user.Email || "",
        profilePicture: user.ProfilePicture || user.AvatarUrl || null,
        isActive: user.IsActive !== false,
        level: 0,
        hasChildren: true, // We assume root has children, will update after first load
        children: [],
      };

      setRootNode(root);
      setLoading(false);
    } catch (err) {
      console.error("❌ Error initializing root:", err);
      setError("Failed to load community network. Please try again.");
      setLoading(false);
    }
  }, []);

  // Fetch direct referrals for a given referral code
  const fetchDirectReferrals = useCallback(async (referralCode) => {
    if (loadingChildren.has(referralCode) || loadedChildren.has(referralCode)) {
      return; // Already loading or loaded
    }

    try {
      setLoadingChildren((prev) => new Set(prev).add(referralCode));

      const token = localStorage.getItem("token");
      const url = API.directReferrals(referralCode);
      console.log(`📡 [fetchDirectReferrals] Fetching from: ${url}`);
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(`✅ [fetchDirectReferrals] Response received:`, response.data);
      const referrals = response.data?.referrals || [];
      console.log(`📊 [fetchDirectReferrals] Found ${referrals.length} referrals`);
      
      // DEBUG: Log the RAW structure of referrals from API
      if (referrals.length > 0) {
        console.log(`🔍 [fetchDirectReferrals] FIRST REFERRAL RAW STRUCTURE:`, JSON.stringify({
          UserName: referrals[0].UserName,
          ReferralCode: referrals[0].ReferralCode,
          Level: referrals[0].Level,
          hasChildren: 'children' in referrals[0],
          childrenType: typeof referrals[0].children,
          childrenIsArray: Array.isArray(referrals[0].children),
          childrenLength: referrals[0].children?.length || 0,
          childrenRaw: referrals[0].children ? referrals[0].children.slice(0, 2) : null
        }, null, 2));
        
        // Check if flat data is available - THIS SHOWS ALL DESCENDANTS
        if (response.data?.flat && response.data.flat.length > 0) {
          const level1Count = response.data.flat.filter(r => r.Level === 1).length;
          const level2Count = response.data.flat.filter(r => r.Level === 2).length;
          const level3Count = response.data.flat.filter(r => r.Level === 3).length;
          const maxLevel = Math.max(...response.data.flat.map(r => r.Level));
          console.log(`📊 [fetchDirectReferrals] FLAT DATA (all descendants): total=${response.data.flat.length}, L1=${level1Count}, L2=${level2Count}, L3=${level3Count}, maxLevel=${maxLevel}`);
          
          // Show sample level 2 records to verify they exist
          const level2Samples = response.data.flat.filter(r => r.Level === 2).slice(0, 3);
          if (level2Samples.length > 0) {
            console.log(`📊 [fetchDirectReferrals] Sample Level 2 records:`, level2Samples.map(r => ({
              UserName: r.UserName,
              ReferralCode: r.ReferralCode,
              ReferredByCode: r.ReferredByCode,
              Level: r.Level
            })));
          }
        }
      }
      
      // Helper function to convert referral node to tree node format (handles nested children)
      const convertToTreeNode = (ref) => {
        const hasChildren = ref.children && Array.isArray(ref.children) && ref.children.length > 0;
        const refCode = ref.ReferralCode ? normalizeCode(ref.ReferralCode) : '';
        const totalReferrals = referralCounts.get(refCode) || 0;
        const node = {
          id: `user-${ref.UserID}`,
          userId: ref.UserID,
          referralCode: ref.ReferralCode,
          name: `${ref.FirstName || ""} ${ref.LastName || ""}`.trim() || ref.UserName || "Unknown",
          userName: ref.UserName || "",
          firstName: ref.FirstName || "",
          lastName: ref.LastName || "",
          email: ref.Email || "",
          profilePicture: null, // Add if available in response
          isActive: ref.IsActive !== false,
          level: ref.Level || 1,
          hasChildren: hasChildren,
          totalReferrals: totalReferrals, // Total count of all referrals (descendants)
          children: hasChildren ? ref.children.map(child => convertToTreeNode(child)) : [],
        };
        return node;
      };
      
      // WORKAROUND: If referrals don't have nested children but flat data does, rebuild tree from flat data
      let finalReferrals = referrals;
      const flatData = response.data?.flat || [];
      
      // Helper to normalize codes (define early so it can be used throughout)
      const normalizeCode = (code) => code ? code.trim().toUpperCase() : '';
      
      console.log(`🔍 [fetchDirectReferrals] Checking flat data: exists=${!!response.data?.flat}, length=${flatData.length}`);
      console.log(`🔍 [fetchDirectReferrals] Response structure:`, {
        hasReferrals: !!response.data?.referrals,
        referralsLength: referrals.length,
        hasFlat: !!response.data?.flat,
        flatLength: flatData.length,
        totalCount: response.data?.totalCount,
        level1Count: response.data?.level1Count,
        nestedCount: response.data?.nestedCount
      });
      
      // Calculate total referral counts for each user from flat data
      // Count ALL descendants (not just direct referrals) by traversing the tree
      const referralCounts = new Map();
      
      // Helper function to count all descendants recursively
      const countDescendants = (targetCode, data) => {
        let count = 0;
        const normalizedTarget = normalizeCode(targetCode);
        
        // Count direct referrals (level 1)
        const direct = data.filter(ref => 
          ref.ReferredByCode && normalizeCode(ref.ReferredByCode) === normalizedTarget
        );
        count += direct.length;
        
        // Recursively count descendants of direct referrals
        direct.forEach(child => {
          if (child.ReferralCode) {
            count += countDescendants(child.ReferralCode, data);
          }
        });
        
        return count;
      };
      
      // Initialize all referral codes and calculate their total referral counts
      const allReferralCodes = new Set();
      
      // Collect all referral codes from flat data
      flatData.forEach((ref) => {
        if (ref.ReferralCode) {
          allReferralCodes.add(ref.ReferralCode);
        }
      });
      
      // Also add level 1 referrals from the referrals array
      referrals.forEach((ref) => {
        if (ref.ReferralCode) {
          allReferralCodes.add(ref.ReferralCode);
        }
      });
      
      // Calculate total referral count for each referral code
      allReferralCodes.forEach((code) => {
        const count = countDescendants(code, flatData);
        referralCounts.set(normalizeCode(code), count);
      });
      
      console.log(`📊 [fetchDirectReferrals] Calculated referral counts for ${referralCounts.size} users`);
      // Log sample counts
      Array.from(referralCounts.entries()).slice(0, 5).forEach(([code, count]) => {
        console.log(`   - ${code}: ${count} total referrals`);
      });
      
      // If flat data is missing but we know descendants exist, we need to fetch them differently
      // For now, if referrals don't have children and flat is missing, try to fetch children for each level 1
      if (flatData.length === 0 && referrals.length > 0 && referrals[0].children?.length === 0) {
        console.log(`⚠️ [fetchDirectReferrals] No flat data and no nested children. Backend may not be sending descendants.`);
        console.log(`💡 [fetchDirectReferrals] This suggests the backend is using the fallback stored procedure (level 1 only).`);
        console.log(`💡 [fetchDirectReferrals] Check backend logs to see if mlm_GetDirectReferralsWithDescendants is being called.`);
      }
      
      if (flatData.length > 0 && referrals.length > 0) {
        const level1Count = referrals.length;
        const level2PlusCount = flatData.filter(r => r.Level > 1).length;
        const referralsChildrenLength = referrals[0]?.children?.length || 0;
        
        console.log(`🔍 [fetchDirectReferrals] Workaround check: level2PlusCount=${level2PlusCount}, referrals[0].children.length=${referralsChildrenLength}`);
        console.log(`🔍 [fetchDirectReferrals] Flat data sample (first 3):`, flatData.slice(0, 3).map(r => ({
          UserName: r.UserName,
          Level: r.Level,
          ReferralCode: r.ReferralCode,
          ReferredByCode: r.ReferredByCode
        })));
        
        // ALWAYS rebuild if flat data has more records than just level 1
        if (level2PlusCount > 0) {
          console.log(`🔧 [fetchDirectReferrals] WORKAROUND: Rebuilding tree from flat data (${flatData.length} total records, ${level2PlusCount} level 2+)`);
          console.log(`🔧 [fetchDirectReferrals] WORKAROUND: Rebuilding tree from flat data (${flatData.length} total records)`);
          
          // Build a map of all referrals by ReferralCode
          const refMap = new Map();
          flatData.forEach(ref => {
            refMap.set(ref.ReferralCode?.toUpperCase(), ref);
          });
          
          // Build tree structure from flat data
          const buildTreeFromFlat = (parentCode) => {
            const children = [];
            flatData.forEach(ref => {
              if (ref.ReferredByCode && normalizeCode(ref.ReferredByCode) === normalizeCode(parentCode)) {
                const child = {
                  ...ref,
                  children: buildTreeFromFlat(ref.ReferralCode)
                };
                children.push(child);
              }
            });
            return children;
          };
          
          // Rebuild referrals with nested children
          finalReferrals = referrals.map(l1Ref => ({
            ...l1Ref,
            children: buildTreeFromFlat(l1Ref.ReferralCode)
          }));
          
          console.log(`✅ [fetchDirectReferrals] Rebuilt tree: ${finalReferrals.length} level 1 with nested children`);
          finalReferrals.forEach(l1 => {
            console.log(`   - ${l1.UserName}: ${l1.children.length} direct children`);
          });
        }
      }
      
      // Convert to tree node format (recursively handle nested children)
      const children = finalReferrals.map((ref) => convertToTreeNode(ref));
      
      console.log(`🌳 [fetchDirectReferrals] Converted ${children.length} referrals with nested children`);
      // Log children count for debugging - VERIFY THE STRUCTURE
      let totalDescendants = 0;
      const logChildren = (nodes, depth = 0) => {
        nodes.forEach((child) => {
          const childCount = child.children?.length || 0;
          if (childCount > 0) {
            totalDescendants += childCount;
            console.log(`${'  '.repeat(depth)}   ✅ ${child.userName} (Level ${child.level}, Code: ${child.referralCode}): ${childCount} direct children`);
            // Log first child details
            if (child.children[0]) {
              console.log(`${'  '.repeat(depth + 1)}   → First child: ${child.children[0].userName} (Level ${child.children[0].level})`);
            }
            logChildren(child.children, depth + 1);
          } else {
            console.log(`${'  '.repeat(depth)}   ❌ ${child.userName} (Level ${child.level}): NO children`);
          }
        });
      };
      logChildren(children);
      console.log(`📊 [fetchDirectReferrals] Total descendants loaded: ${totalDescendants}`);
      
      // VERIFY: Check if children actually have the children property
      if (children.length > 0) {
        const firstL1 = children[0];
        console.log(`🔍 [fetchDirectReferrals] VERIFICATION - First Level 1 node:`, {
          userName: firstL1.userName,
          level: firstL1.level,
          referralCode: firstL1.referralCode,
          hasChildrenProp: 'children' in firstL1,
          childrenIsArray: Array.isArray(firstL1.children),
          childrenLength: firstL1.children?.length || 0,
          firstChild: firstL1.children?.[0] ? {
            userName: firstL1.children[0].userName,
            level: firstL1.children[0].level
          } : null
        });
      }

      // Store children in the map
      setLoadedChildren((prev) => {
        const newMap = new Map(prev);
        newMap.set(referralCode, children);
        
        // Also store nested children in the map by their parent's referral code
        // This allows the tree to display them when nodes are expanded
        let nestedStoredCount = 0;
        const storeNestedChildren = (nodes, parentCode) => {
          nodes.forEach((node) => {
            if (node.children && node.children.length > 0) {
              // Store this node's children
              newMap.set(node.referralCode, node.children);
              nestedStoredCount++;
              console.log(`💾 [fetchDirectReferrals] Stored children for ${node.userName} (${node.referralCode}): ${node.children.length} children`);
              // Recursively store children of children
              storeNestedChildren(node.children, node.referralCode);
            }
          });
        };
        storeNestedChildren(children, referralCode);
        console.log(`💾 [fetchDirectReferrals] Stored ${nestedStoredCount} nested referral codes in loadedChildren map`);
        
        return newMap;
      });

      // Update root node if it's the root
      if (referralCode === currentUserCode && rootNode) {
        // Root's total referrals is all records in flat data (all descendants)
        const rootRefCode = normalizeCode(referralCode);
        const rootCount = referralCounts.get(rootRefCode) || flatData.length;
        
        setRootNode((prev) => ({
          ...prev,
          children: children,
          hasChildren: children.length > 0,
          totalReferrals: rootCount, // Total count of all descendants
        }));
      }
    } catch (err) {
      console.error(`❌ Error fetching direct referrals for ${referralCode}:`, err);
      console.error(`❌ Error details:`, {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: err.config?.url
      });
      // Set empty array on error so we don't keep retrying
      setLoadedChildren((prev) => {
        const newMap = new Map(prev);
        newMap.set(referralCode, []);
        return newMap;
      });
    } finally {
      setLoadingChildren((prev) => {
        const newSet = new Set(prev);
        newSet.delete(referralCode);
        return newSet;
      });
    }
  }, [currentUserCode, rootNode, loadingChildren, loadedChildren]);

  // Fetch ads watched data when root node is initialized
  useEffect(() => {
    if (rootNode?.referralCode && currentUserCode && !loadingChildren.has(rootNode.referralCode) && !loadedChildren.has(rootNode.referralCode)) {
      // Fetch ads watched data for the root user and their referral tree
      fetchDirectReferrals(rootNode.referralCode);
    }
  }, [rootNode?.referralCode, currentUserCode, fetchDirectReferrals, loadingChildren, loadedChildren]);

  // Build complete tree structure with all loaded children
  const buildCompleteTree = useCallback((node) => {
    if (!node) return null;

    // Get children from loadedChildren map (most up-to-date) OR use node's children if available
    // Priority: loadedChildren map > node.children > empty array
    let children = loadedChildren.get(node.referralCode);
    if (!children || children.length === 0) {
      children = node.children || [];
    }

    // Build complete tree recursively - children may already have nested children from API
    const builtChildren = children.map((child) => buildCompleteTree(child));
    
    if (children.length > 0 && node.level <= 1) {
      console.log(`🌳 [buildCompleteTree] Built tree for ${node.userName} (Level ${node.level}): ${builtChildren.length} children`);
    }
    
    return {
      ...node,
      children: builtChildren,
    };
  }, [loadedChildren]);

  // Find node by tree ID recursively
  const findNodeById = useCallback((node, targetId) => {
    if (!node) return null;
    if (node.id === targetId) return node;

    // Check children from loadedChildren map
    const children = loadedChildren.get(node.referralCode) || node.children || [];
    for (const child of children) {
      const found = findNodeById(child, targetId);
      if (found) return found;
    }
    return null;
  }, [loadedChildren]);

  const toggleNode = useCallback(
    (nodeId) => {
      const wasExpanded = expandedNodes.has(nodeId);
      const node = findNodeById(rootNode, nodeId);
      if (!wasExpanded && node?.referralCode && !loadedChildren.has(node.referralCode)) {
        fetchDirectReferrals(node.referralCode);
      }
      setExpandedNodes((prev) => {
        const next = new Set(prev);
        if (wasExpanded) next.delete(nodeId);
        else next.add(nodeId);
        return next;
      });
    },
    [expandedNodes, rootNode, findNodeById, loadedChildren, fetchDirectReferrals]
  );

  /** Lightweight org chart: parent on top, children in a horizontal row with connector lines. */
  const renderOrgSubtree = useCallback(
    (node, depth) => {
      if (!node) return null;

      const isRoot = node.level === 0;
      const isLoading = loadingChildren.has(node.referralCode);
      let children = loadedChildren.get(node.referralCode);
      if (!children || children.length === 0) {
        children = node.children || [];
      }
      const hasChildren =
        children.length > 0 || (!loadedChildren.has(node.referralCode) && node.hasChildren);
      const expanded = expandedNodes.has(node.id);

      const displayFirstName =
        (node.firstName && String(node.firstName).trim()) ||
        (node.name && String(node.name).trim().split(/\s+/)[0]) ||
        node.name ||
        "?";
      const avatarLetter = displayFirstName.charAt(0).toUpperCase();

      return (
        <Box
          key={node.id}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: { xs: 128, sm: 134 },
            maxWidth: { xs: 138, sm: 146 },
            position: "relative",
            flexShrink: 0,
            mx: "auto",
          }}
        >
          <Paper
            variant="outlined"
            elevation={0}
            data-node-id={node.id}
            role={hasChildren ? "button" : undefined}
            tabIndex={hasChildren ? 0 : undefined}
            onClick={() => hasChildren && toggleNode(node.id)}
            onKeyDown={(e) => {
              if (!hasChildren) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleNode(node.id);
              }
            }}
            sx={{
              width: "100%",
              borderRadius: 0.75,
              overflow: "hidden",
              borderColor: highlightedId === node.id ? "secondary.main" : isRoot ? "primary.main" : "divider",
              bgcolor: highlightedId === node.id ? "action.selected" : isRoot ? "action.hover" : "background.paper",
              boxShadow: highlightedId === node.id ? 3 : 0,
              opacity: node.isActive === false ? 0.85 : 1,
              cursor: hasChildren ? "pointer" : "default",
              "&:focus-visible": hasChildren
                ? { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 }
                : undefined,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.35,
                py: 0.25,
                px: { xs: 0.4, sm: 0.5 },
                flexWrap: "nowrap",
                width: "100%",
              }}
            >
              {hasChildren && (
                <ExpandMoreIcon
                  aria-hidden
                  sx={{
                    flexShrink: 0,
                    width: 16,
                    height: 16,
                    color: "text.secondary",
                    transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.15s ease",
                  }}
                />
              )}
              {!hasChildren && <Box sx={{ width: 16, flexShrink: 0 }} aria-hidden />}
              <Avatar
                src={node.profilePicture}
                sx={{
                  width: 18,
                  height: 18,
                  bgcolor: isRoot ? "primary.main" : "secondary.main",
                  fontSize: "0.58rem",
                  flexShrink: 0,
                }}
              >
                {avatarLetter}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={isRoot ? "bold" : "medium"}
                  noWrap
                  sx={{ fontSize: "0.62rem", lineHeight: 1.1, display: "block" }}
                >
                  {displayFirstName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: "0.5rem", lineHeight: 1.05, display: "block" }}
                >
                  @{node.userName}
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={0.2}
                sx={{
                  flexShrink: 0,
                  flexWrap: "nowrap",
                  alignItems: "center",
                }}
              >
                {isRoot && (
                  <Chip
                    label="You"
                    size="small"
                    color="primary"
                    sx={{
                      height: 14,
                      fontSize: "0.45rem",
                      "& .MuiChip-label": { px: 0.35 },
                    }}
                  />
                )}
                {!isRoot && (
                  <Chip
                    label={`L${node.level}`}
                    size="small"
                    color={node.level <= 2 ? "success" : node.level <= 5 ? "info" : "default"}
                    sx={{
                      height: 14,
                      fontSize: "0.45rem",
                      "& .MuiChip-label": { px: 0.35 },
                    }}
                  />
                )}
                {!node.isActive && (
                  <Chip
                    label="Inactive"
                    size="small"
                    color="error"
                    sx={{
                      height: 14,
                      fontSize: "0.45rem",
                      "& .MuiChip-label": { px: 0.35 },
                    }}
                  />
                )}
                {isLoading && <CircularProgress size={8} sx={{ display: { xs: "none", sm: "block" } }} />}
              </Stack>
            </Box>
          </Paper>

          {expanded && isLoading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading referrals...
              </Typography>
            </Box>
          )}

          {expanded &&
            !isLoading &&
            hasChildren &&
            children.length === 0 &&
            loadedChildren.has(node.referralCode) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 0.75, fontStyle: "italic" }}>
                No referrals yet
              </Typography>
            )}

          {expanded && !isLoading && hasChildren && children.length === 0 && !loadedChildren.has(node.referralCode) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 0.75, fontStyle: "italic" }}>
              No children found (but hasChildren=true)
            </Typography>
          )}

          {expanded && !isLoading && children.length > 0 && (
            <Box sx={{ mt: 1.5, pt: 1.5, position: "relative", display: "flex", justifyContent: "center", width: "100%" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 2,
                  height: 20,
                  bgcolor: "divider",
                }}
              />
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  gap: 2,
                  width: "max-content",
                  minWidth: "100%",
                  px: 1,
                  ...(children.length > 1 && {
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: `${50 / children.length}%`,
                      right: `${50 / children.length}%`,
                      height: 2,
                      bgcolor: "divider",
                    },
                  }),
                }}
              >
                {children.map((child) => (
                  <Box
                    key={child.id}
                    sx={{
                      pt: 1.5,
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 2,
                        height: 12,
                        bgcolor: "divider",
                      },
                    }}
                  >
                    {renderOrgSubtree(child, depth + 1)}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      );
    },
    [expandedNodes, loadedChildren, loadingChildren, toggleNode, highlightedId]
  );

  const handleSearch = useCallback(() => {
    const q = searchTerm.trim();
    if (searchExpandTimerRef.current) {
      clearTimeout(searchExpandTimerRef.current);
      searchExpandTimerRef.current = null;
    }

    if (!q || !rootNode) {
      setHighlightedId(null);
      setSearchMessage("");
      return;
    }

    if (currentUserCode && loadingChildren.has(currentUserCode)) {
      setSearchMessage("Tree is still loading. Try again in a moment.");
      return;
    }

    const tree = buildCompleteTree(rootNode);
    const path = findPathToMatch(tree, q);
    if (!path || path.length === 0) {
      setHighlightedId(null);
      setSearchMessage("No member found in your loaded network.");
      return;
    }

    const ancestors = path.slice(0, -1);
    const matchId = path[path.length - 1];
    const wasExpanded = expandedNodes.size > 0;
    setSearchMessage("");
    setHighlightedId(null);

    const expandMatchPath = () => {
      setExpandedNodes(new Set(ancestors));
      setHighlightedId(matchId);
    };

    if (wasExpanded) {
      setExpandedNodes(new Set());
      searchExpandTimerRef.current = setTimeout(() => {
        searchExpandTimerRef.current = null;
        expandMatchPath();
      }, 350);
      return;
    }

    expandMatchPath();
  }, [searchTerm, rootNode, currentUserCode, loadingChildren, buildCompleteTree, expandedNodes]);

  useEffect(() => {
    if (!highlightedId) return undefined;
    const frame = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-node-id="${CSS.escape(highlightedId)}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    });
    return () => cancelAnimationFrame(frame);
  }, [highlightedId, expandedNodes]);

  useEffect(() => () => {
    if (searchExpandTimerRef.current) {
      clearTimeout(searchExpandTimerRef.current);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    initializeRoot();
  }, [initializeRoot]);

  // Auto-load root's children when root is set
  useEffect(() => {
    if (rootNode && currentUserCode && !loadedChildren.has(currentUserCode)) {
      fetchDirectReferrals(currentUserCode);
    }
  }, [rootNode, currentUserCode, loadedChildren, fetchDirectReferrals]);

  // Build complete tree structure with all loaded children (MUST be before conditional returns)
  const treeData = useMemo(() => {
    return rootNode ? buildCompleteTree(rootNode) : null;
  }, [rootNode, buildCompleteTree]);

  // Calculate total loaded referrals (MUST be before conditional returns)
  const totalReferrals = useMemo(() => {
    if (!treeData) return 0;
    const countReferrals = (node) => {
      if (!node) return 0;
      const children = loadedChildren.get(node.referralCode) || [];
      return children.length + children.reduce((sum, child) => sum + countReferrals(child), 0);
    };
    return countReferrals(treeData);
  }, [treeData, loadedChildren]);

  useEffect(() => {
    if (!treeData?.id) return;
    if (lastExpandedTreeRootId.current === treeData.id) return;
    lastExpandedTreeRootId.current = treeData.id;
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      next.add(treeData.id);
      return next;
    });
  }, [treeData?.id]);

  /** Wider than viewport: start horizontally centered so the left side is not clipped at scroll 0. */
  useLayoutEffect(() => {
    if (!treeData) return;
    const el = orgChartScrollRef.current;
    if (!el) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (el.scrollWidth > el.clientWidth) {
          el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
        } else {
          el.scrollLeft = 0;
        }
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [treeData?.id, totalReferrals, loadedChildren.size, loading]);

  // All hooks must be called before any conditional returns
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!rootNode) {
    return (
      <Box mt={3}>
        <Alert severity="info">No community network found.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <DashboardPageHeader
        icon={AccountTreeIcon}
        title="Community network"
        subtitle="Explore your referral tree, search members, and refresh the latest structure."
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={1} 
          alignItems="stretch"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          <TextField
            label="Search by name or username"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (searchMessage) setSearchMessage("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            error={Boolean(searchMessage)}
            helperText={searchMessage || " "}
            sx={{ 
              width: { xs: '100%', sm: 300 },
              minWidth: { xs: '100%', sm: 250 }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleSearch} edge="end">
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            component={RouterLink}
            to="/dashboard/earnings-calculator"
            variant="outlined"
            size="small"
            startIcon={<CalculateIcon />}
            sx={{ alignSelf: { xs: "stretch", sm: "center" }, whiteSpace: "nowrap" }}
          >
            Earnings calculator
          </Button>
          <IconButton 
            onClick={initializeRoot} 
            title="Refresh"
            sx={{ 
              alignSelf: { xs: 'flex-start', sm: 'center' },
              minWidth: { xs: '40px', sm: 'auto' }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Stack>
      </DashboardPageHeader>

      {/* Stats */}
      <Paper 
        sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          mb: 2,
          borderRadius: { xs: 2, sm: 3 }
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              Total Community Connections
            </Typography>
            <Typography 
              variant="caption" 
              color="text.secondary"
              component="div"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, mt: 0.25, lineHeight: 1.3 }}
            >
              {`All downline accounts in your referral chain (levels 1–${COMMUNITY_TOTAL_DEPTH}), same as Engagement Summary`}
            </Typography>
            <Typography 
              variant="h6" 
              color="primary"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mt: 0.5 }}
            >
              {totalCommunityConnections.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              {`Loaded Connections (up to Level ${COMMUNITY_NETWORK_MAX_DEPTH})`}
            </Typography>
            <Typography 
              variant="h6" 
              color="secondary"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              {totalReferrals.toLocaleString()}
            </Typography>
            <Chip
              label={`Showing up to Level ${COMMUNITY_NETWORK_MAX_DEPTH}`}
              size="small"
              color="info"
              variant="outlined"
              sx={{ 
                mt: 0.5,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                height: { xs: '20px', sm: '24px' }
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, mt: 0.75, lineHeight: 1.35 }}
            >
              Active members through level {COMMUNITY_NETWORK_MAX_DEPTH} in this list; levels{' '}
              {COMMUNITY_NETWORK_MAX_DEPTH + 1}–{COMMUNITY_TOTAL_DEPTH} are included in Total only.
            </Typography>
          </Box>
        </Stack>
        {communityLevelCounts.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.5 }}>
            {communityLevelCounts.slice(0, COMMUNITY_TOTAL_DEPTH).map(({ level, memberCount }) => (
              <Chip
                key={`community-level-${level}`}
                size="small"
                variant="outlined"
                label={`L${level}: ${Number(memberCount).toLocaleString()}`}
                sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
              />
            ))}
            {communityMaxDepth > 0 && (
              <Chip
                size="small"
                color="default"
                variant="filled"
                label={`Max depth: ${communityMaxDepth}`}
                sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
              />
            )}
          </Stack>
        )}
      </Paper>

      {/* Tree View */}
      <Paper
        ref={orgChartScrollRef}
        sx={{
          p: { xs: 1, sm: 2 },
          minHeight: { xs: 400, sm: 500 },
          maxHeight: { xs: "60vh", sm: "70vh" },
          overflow: "auto",
          borderRadius: { xs: 2, sm: 3 },
          display: "flex",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        <Box
          component="section"
          aria-label="Referral hierarchy"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "max-content",
            mx: "auto",
            px: { xs: 1, sm: 2 },
            py: 0.5,
            boxSizing: "border-box",
          }}
        >
          {renderOrgSubtree(treeData, 0)}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReferralTreeLazy;

